import { Modal } from '../../Modal';
import { Button, ScrollArea, Switch, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import {
  setCreditsWidgetSim,
  setCreditsWidgetSimDelay,
  useCreditsWidgetSim,
  useCreditsWidgetSimDelay,
  SIM_DELAY_MESSAGE_THRESHOLD,
  type CreditsWidgetSim,
} from '../../../lib/credits-widget-sim';

// Dev-only simulator for the low/out-of-credits widgets. Forcing a state here substitutes the credit
// DATA (see `useCreditsWidgetState`) and repaints every surface LIVE — no reload — so you can watch the
// chat card + sticky bar appear mid-conversation.

// One row per concrete state. Daily is always the free plan; monthly splits three ways by tier — Free
// (no plan/date, "Upgrade"), Paid (names the plan + renewal date, "Upgrade Plan") and Max+ (date-free
// "Add credits…" body, "Add Credits").
const OPTIONS: { value: CreditsWidgetSim; label: string; hint: string }[] = [
  { value: 'low-daily', label: 'Low · Daily', hint: 'Free plan — 70% of the daily limit spent' },
  { value: 'out-daily', label: 'Out · Daily', hint: 'Free plan — daily limit reached' },
  { value: 'low-monthly-free', label: 'Low · Monthly · Free', hint: 'Free plan — 70% of monthly credits spent' },
  { value: 'out-monthly-free', label: 'Out · Monthly · Free', hint: 'Free plan — monthly credits exhausted' },
  { value: 'low-monthly', label: 'Low · Monthly · Paid', hint: 'Paid plan — names plan + renewal date' },
  { value: 'out-monthly', label: 'Out · Monthly · Paid', hint: 'Paid plan — names plan + renewal date' },
  { value: 'low-monthly-maxplus', label: 'Low · Monthly · Max+', hint: 'Top tier — “Add Credits” CTA' },
  { value: 'out-monthly-maxplus', label: 'Out · Monthly · Max+', hint: 'Top tier — “Add Credits” CTA' },
];

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevCreditsWidgetPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <CreditsWidgetPanel {...props} />;
}

function CreditsWidgetPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sim = useCreditsWidgetSim();
  const simDelay = useCreditsWidgetSimDelay();

  const selectOption = (value: CreditsWidgetSim) =>
    setCreditsWidgetSim(sim === value ? null : value); // tapping the active one turns the sim off

  return (
    <Modal open={open} onClose={onClose} width={520} title="Credits widgets" footer={<Button onClick={onClose}>Done</Button>}>
      <Typography variant="p3" color="secondary">
        Force a low/out-of-credits state in THIS browser to preview the drawer card, the inner-screen
        banner and the in-chat widget. It substitutes the credit balance, applied live — open a chat and
        pick a state to watch the chat card and sticky bar appear mid-conversation. Inert without a
        dev-tools grant.
      </Typography>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12, padding: '10px 12px', borderRadius: 12, background: 'var(--ds-surface-standard-bg)' }}>
        <Typography variant="p3" color="primary" component="span">
          Appear only after {SIM_DELAY_MESSAGE_THRESHOLD} messages sent
        </Typography>
        <Switch checked={simDelay} onChange={(next) => setCreditsWidgetSimDelay(next)} label="Delay" />
      </div>

      <ScrollArea style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, maxHeight: 360 }}>
        <Button fullWidth variant={sim === null ? 'contained' : 'outlined'} onClick={() => setCreditsWidgetSim(null)}>
          Off · use the real balance
        </Button>
        {OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            fullWidth
            variant={sim === opt.value ? 'contained' : 'outlined'}
            onClick={() => selectOption(opt.value)}
          >
            {opt.label} — {opt.hint}
          </Button>
        ))}
      </ScrollArea>
    </Modal>
  );
}
